/* B Store theme behaviour: header, hero swap, rails, quick view, cart and motion. */

/* ---- 00-core.js ---- */
/* Core helpers shared by every B Store module. */
(() => {
  'use strict';

  const BS = (window.BStore = window.BStore || {});
  const C = (BS.config = BS.config || {});
  C.routes = C.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', products: '/products/' };
  C.strings = C.strings || {};

  const $ = (s, c) => (c || document).querySelector(s);
  const $$ = (s, c) => Array.from((c || document).querySelectorAll(s));
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  BS.$ = $;
  BS.$$ = $$;
  BS.clamp = clamp;

  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fineQuery = window.matchMedia('(pointer: fine)');
  BS.reduced = () => reduceQuery.matches || C.motion === false;
  BS.fine = () => fineQuery.matches;
  BS.gsap = () => (typeof window.gsap !== 'undefined' ? window.gsap : null);
  BS.ST = () => (typeof window.ScrollTrigger !== 'undefined' ? window.ScrollTrigger : null);
  BS.designMode = () => Boolean((window.Shopify && window.Shopify.designMode) || C.designMode);
  BS.desktop = () => window.matchMedia('(min-width: 900px)').matches;

  /* ---------- money ---------- */
  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return whole + cents;
  };

  BS.money = (cents, format) => {
    const fmt = format || C.moneyFormat || '{{amount}}';
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount': return formatWithDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return formatWithDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return formatWithDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatWithDelimiters(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return formatWithDelimiters(value, 0, ' ', '.');
        case 'amount_with_space_separator': return formatWithDelimiters(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return formatWithDelimiters(value, 2, ' ', '.');
        default: return formatWithDelimiters(value, 2, ',', '.');
      }
    }).replace(/<[^>]*>/g, '');
  };

  BS.esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

  BS.t = (key, vars) => {
    let s = C.strings[key] || '';
    if (vars) Object.keys(vars).forEach((k) => { s = s.split('%' + k + '%').join(vars[k]).split('{{ ' + k + ' }}').join(vars[k]).split('{{' + k + '}}').join(vars[k]); });
    return s;
  };

  /* ---------- toast ---------- */
  let toastEl = null;
  BS.toast = (msg) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'bs-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-on');
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(() => toastEl.classList.remove('is-on'), 2200);
  };

  /* ---------- scroll lock shared with Horizon's drawers ---------- */
  let utilities = null;
  const loadUtilities = () => {
    if (!utilities) utilities = import('@theme/utilities').catch(() => null);
    return utilities;
  };
  BS.lock = async (owner) => {
    const u = await loadUtilities();
    if (u && u.lockScroll) u.lockScroll(owner);
    else document.documentElement.setAttribute('scroll-lock', '');
  };
  BS.unlock = async (owner) => {
    const u = await loadUtilities();
    if (u && u.unlockScroll) u.unlockScroll(owner);
    else document.documentElement.removeAttribute('scroll-lock');
  };

  /* ---------- section registry (works with the theme editor) ---------- */
  const registry = {};
  const instances = new WeakMap();

  BS.register = (type, mod) => {
    registry[type] = mod;
  };

  BS.initSection = (el) => {
    const type = el.dataset.bsSection;
    const mod = registry[type];
    if (!mod || instances.has(el)) return;
    try {
      const inst = mod.init(el) || {};
      instances.set(el, inst);
    } catch (err) {
      console.error('[bstore] ' + type + ' failed to start', err);
    }
  };

  BS.destroySection = (el) => {
    const inst = instances.get(el);
    if (!inst) return;
    try {
      if (inst.destroy) inst.destroy();
    } catch (err) {
      console.warn('[bstore] destroy', err);
    }
    instances.delete(el);
  };

  BS.instance = (el) => instances.get(el);

  BS.initAll = (root) => {
    $$('[data-bs-section]', root || document).forEach(BS.initSection);
  };

  BS.allSections = () => $$('[data-bs-section]');
})();

/* ---- 10-cart.js ---- */
/* Cart: adds through the Ajax Cart API and tells Horizon's cart drawer, cart icon and cart page
   about it with the same standard event the theme's own product form uses. */
(() => {
  'use strict';

  const BS = window.BStore;
  const C = BS.config;
  const { $$ } = BS;

  let eventsModule = null;
  const loadEvents = () => {
    if (!eventsModule) eventsModule = import('@shopify/events').catch(() => null);
    return eventsModule;
  };

  const drawer = () => document.querySelector('theme-drawer#cart-drawer');

  const fetchCart = () =>
    fetch(C.routes.cart + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());

  const openDrawer = () => {
    const d = drawer();
    if (d && typeof d.open === 'function') {
      if (!d.hasAttribute('open')) d.open();
      return true;
    }
    return false;
  };

  /**
   * Adds lines to the cart.
   * @param {{id: number|string, quantity?: number}[]} items
   * @param {{source?: Element, context?: string, productId?: number|string}} [opts]
   */
  const add = async (items, opts = {}) => {
    const source = opts.source && opts.source.isConnected ? opts.source : document.body;
    const lines = items.map((i) => ({ id: Number(i.id), quantity: Math.max(1, Number(i.quantity) || 1) }));
    const itemCount = lines.reduce((s, l) => s + l.quantity, 0);
    const sectionIds = $$('cart-items-component')
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const mod = await loadEvents();
    const Ev = mod && mod.CartLinesUpdateEvent;
    let deferred = null;

    if (Ev) {
      deferred = Ev.createPromise();
      source.dispatchEvent(
        new Ev({
          action: 'add',
          context: opts.context || 'product',
          lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
          promise: deferred.promise,
        })
      );
    }

    const body = { items: lines };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    let response;
    let data;
    try {
      response = await fetch(C.routes.cartAdd + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      data = await response.json();
    } catch (err) {
      data = { status: 500, description: C.strings.error };
    }

    const failed = !response || !response.ok || (data && data.status);
    let cart = null;
    try {
      cart = await fetchCart();
    } catch (err) {
      cart = null;
    }

    if (deferred) {
      deferred.resolve({
        cart: cart ? Ev.createCartFromAjaxResponse(cart) : null,
        detail: {
          items: cart ? cart.items : [],
          source: 'bstore',
          sourceId: 'bstore',
          itemCount,
          productId: opts.productId ? String(opts.productId) : undefined,
          sections: failed ? undefined : data.sections,
          didError: Boolean(failed),
        },
      });
    }

    if (failed) {
      const message = (data && (data.description || data.message)) || C.strings.error;
      const error = new Error(message);
      error.data = data;
      throw error;
    }

    document.dispatchEvent(new CustomEvent('bstore:cart:added', { detail: { cart, items: lines } }));

    /* Horizon opens the drawer itself when "auto open" is on; this covers the setting being off */
    if (opts.open === false) return cart;
    setTimeout(() => {
      if (!openDrawer()) {
        if (C.template === 'cart') window.location.reload();
        else BS.toast(C.strings.added || 'Added');
      }
    }, 60);

    return cart;
  };

  BS.cart = { add, open: openDrawer, fetch: fetchCart };

  /* single-variant quick add buttons on cards and in the cart cross-sell */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-bs-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await add([{ id: btn.dataset.bsQuickAdd, quantity: 1 }], { source: btn, productId: btn.dataset.productId });
    } catch (err) {
      BS.toast(err.message || C.strings.error);
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  });
})();

/* ---- 20-header.js ---- */
/* Header: overlay to glass pill, mega menu, mobile navigation. */
(() => {
  'use strict';

  const BS = window.BStore;
  const { $, $$ } = BS;

  BS.register('header', {
    init(root) {
      const header = $('[data-bs-header]', root);
      if (!header) return {};
      const cleanups = [];
      const on = (el, ev, fn, opt) => {
        el.addEventListener(ev, fn, opt);
        cleanups.push(() => el.removeEventListener(ev, fn, opt));
      };

      /* ---------- pill state ---------- */
      const overlayHost = () => {
        if (!header.classList.contains('can-overlay')) return null;
        const first = document.querySelector('#MainContent > .shopify-section:first-child');
        return first && first.querySelector('[data-bs-overlay]') ? first : null;
      };

      let host = overlayHost();
      let pill = !host;
      const setPill = (v) => {
        pill = v;
        header.classList.toggle('is-pill', v);
        header.classList.toggle('is-scrolled', v || window.scrollY > 8);
        document.documentElement.classList.toggle('bs-header-pill', v);
      };

      const update = () => {
        host = overlayHost();
        if (!host) {
          header.classList.add('is-pill');
          header.classList.toggle('is-scrolled', window.scrollY > 8);
          return;
        }
        const next = host.nextElementSibling;
        const limit = 92;
        let over;
        if (next) over = next.getBoundingClientRect().top <= limit;
        else over = host.getBoundingClientRect().bottom <= limit;
        if (over !== pill) setPill(over);
        else header.classList.toggle('is-scrolled', over || window.scrollY > 8);
      };

      setPill(!host);
      update();
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          update();
        });
      };
      on(window, 'scroll', onScroll, { passive: true });
      on(window, 'resize', onScroll, { passive: true });
      document.addEventListener('bstore:refresh', onScroll);
      cleanups.push(() => document.removeEventListener('bstore:refresh', onScroll));

      /* ---------- mega menu ---------- */
      const links = $$('[data-mega]', header);
      let openPanel = null;
      let closeTimer = null;
      const panelFor = (a) => document.getElementById(a.dataset.mega);

      const closeMega = () => {
        clearTimeout(closeTimer);
        if (!openPanel) return;
        openPanel.classList.remove('is-open');
        const a = header.querySelector('[aria-controls="' + openPanel.id + '"]');
        if (a) a.setAttribute('aria-expanded', 'false');
        openPanel = null;
      };

      const openMega = (a) => {
        clearTimeout(closeTimer);
        const panel = panelFor(a);
        if (!panel) return;
        if (openPanel && openPanel !== panel) {
          openPanel.classList.remove('is-open');
          const prev = header.querySelector('[aria-controls="' + openPanel.id + '"]');
          if (prev) prev.setAttribute('aria-expanded', 'false');
        }
        panel.classList.add('is-open');
        a.setAttribute('aria-expanded', 'true');
        openPanel = panel;
      };

      const scheduleClose = () => {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(closeMega, 180);
      };

      links.forEach((a) => {
        on(a, 'pointerenter', (e) => {
          if (e.pointerType === 'mouse') openMega(a);
        });
        on(a, 'click', (e) => {
          /* touch: first tap opens the panel, second tap follows the link */
          if (a.getAttribute('aria-expanded') !== 'true' && e.pointerType !== 'mouse' && window.matchMedia('(hover: none)').matches) {
            e.preventDefault();
            openMega(a);
          }
        });
        on(a, 'keydown', (e) => {
          if (e.key === 'ArrowDown' || ((e.key === 'Enter' || e.key === ' ') && e.shiftKey)) {
            e.preventDefault();
            openMega(a);
            const first = panelFor(a)?.querySelector('a');
            if (first) first.focus();
          }
        });
        const li = a.closest('.bs-hdr__item');
        if (li) {
          on(li, 'focusout', (e) => {
            if (!li.contains(e.relatedTarget)) scheduleClose();
          });
          on(li, 'focusin', () => {
            if (openPanel && openPanel !== panelFor(a)) closeMega();
          });
        }
      });

      $$('.bs-hdr__link:not([data-mega])', header).forEach((a) => {
        on(a, 'pointerenter', (e) => {
          if (e.pointerType === 'mouse') scheduleClose();
        });
      });

      const inner = $('.bs-hdr__inner', header);
      on(inner, 'pointerleave', (e) => {
        if (e.pointerType === 'mouse') scheduleClose();
      });
      $$('[data-mega-panel]', header).forEach((p) => {
        on(p, 'pointerenter', () => clearTimeout(closeTimer));
      });
      on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && openPanel) {
          const a = header.querySelector('[aria-controls="' + openPanel.id + '"]');
          closeMega();
          if (a && header.contains(document.activeElement)) a.focus();
        }
      });
      on(document, 'pointerdown', (e) => {
        if (openPanel && !header.contains(e.target)) closeMega();
      });

      /* ---------- mobile navigation ---------- */
      const mnav = $('[data-bs-mnav]', root);
      const openBtn = $('[data-bs-mnav-open]', root);
      let closing = null;

      const closeNav = () => {
        if (!mnav || !mnav.open) return;
        mnav.classList.remove('is-open');
        clearTimeout(closing);
        closing = setTimeout(() => {
          mnav.close();
          BS.unlock(mnav);
          if (BS.lenis) BS.lenis.start();
        }, BS.reduced() ? 0 : 420);
      };

      const openNav = () => {
        if (!mnav) return;
        clearTimeout(closing);
        if (!mnav.open) mnav.showModal();
        BS.lock(mnav);
        if (BS.lenis) BS.lenis.stop();
        requestAnimationFrame(() => mnav.classList.add('is-open'));
      };

      if (mnav && openBtn) {
        on(openBtn, 'click', openNav);
        $$('[data-bs-mnav-close]', mnav).forEach((b) => on(b, 'click', closeNav));
        on(mnav, 'cancel', (e) => {
          e.preventDefault();
          closeNav();
        });
        on(mnav, 'click', (e) => {
          if (e.target === mnav) closeNav();
        });
        on(mnav, 'close', () => {
          openBtn.setAttribute('aria-expanded', 'false');
          BS.unlock(mnav);
        });
        on(mnav, 'click', (e) => {
          const a = e.target.closest('a[href]');
          if (a && !a.target) closeNav();
        });
        const mq = window.matchMedia('(min-width: 1061px)');
        const onMq = () => {
          if (mq.matches) closeNav();
        };
        mq.addEventListener('change', onMq);
        cleanups.push(() => mq.removeEventListener('change', onMq));
      }

      /* search and cart fall back to plain navigation if Horizon's components are not ready */
      const searchBtn = $('[data-bs-open-search]', root);
      if (searchBtn) {
        on(searchBtn, 'click', () => {
          setTimeout(() => {
            const modal = document.getElementById('search-modal');
            const dialog = modal && modal.querySelector('dialog');
            if (dialog && !dialog.open && modal && typeof modal.showDialog === 'function') modal.showDialog();
          }, 30);
        });
      }

      return {
        header,
        setPill,
        update,
        destroy() {
          cleanups.forEach((fn) => fn());
          closeMega();
          if (mnav && mnav.open) mnav.close();
        },
      };
    },
  });
})();

/* ---- 30-motion.js ---- */
/* Motion system: cursor, smooth scrolling, headline reveals, counters, centre-opening sections and the section dots. */
(() => {
  'use strict';

  const BS = window.BStore;
  const C = BS.config;
  const { $, $$, clamp } = BS;

  /* ---------- cursor ---------- */
  BS.initCursor = () => {
    const gsap = BS.gsap();
    const cur = document.getElementById('BsCursor');
    if (!cur || !gsap || !BS.fine() || BS.reduced() || BS.designMode()) return;
    document.documentElement.classList.add('bs-has-cursor');
    const ring = $('[data-c-ring]', cur);
    const dot = $('[data-c-dot]', cur);
    const label = $('[data-c-label]', cur);
    const rx = gsap.quickTo(ring, 'x', { duration: 0.55, ease: 'power3.out' });
    const ry = gsap.quickTo(ring, 'y', { duration: 0.55, ease: 'power3.out' });
    const dx = gsap.quickTo(dot, 'x', { duration: 0.12, ease: 'power3.out' });
    const dy = gsap.quickTo(dot, 'y', { duration: 0.12, ease: 'power3.out' });
    let live = false;
    window.addEventListener(
      'pointermove',
      (e) => {
        if (e.pointerType !== 'mouse') return;
        if (!live) {
          live = true;
          gsap.set([ring, dot], { x: e.clientX, y: e.clientY });
          cur.classList.add('is-live');
        }
        rx(e.clientX);
        ry(e.clientY);
        dx(e.clientX);
        dy(e.clientY);
      },
      { passive: true }
    );
    document.addEventListener('pointerover', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const inDialog = target.closest('dialog');
      cur.classList.toggle('is-dialog', Boolean(inDialog));
      const t = target.closest('[data-cursor]');
      const h = target.closest('a, button, .bs-chip, .bs-sw, summary, label, select');
      if (t && t.dataset.cursor) {
        label.textContent = t.dataset.cursor;
        cur.classList.add('is-label');
        cur.classList.remove('is-hover');
      } else {
        cur.classList.remove('is-label');
        cur.classList.toggle('is-hover', Boolean(h));
      }
    });
    document.documentElement.addEventListener('pointerleave', () => cur.classList.remove('is-live'));
    document.documentElement.addEventListener('pointerenter', () => {
      if (live) cur.classList.add('is-live');
    });
  };

  /* ---------- smooth scrolling (desktop, not in the editor) ---------- */
  BS.initLenis = () => {
    const gsap = BS.gsap();
    const ST = BS.ST();
    if (BS.lenis || !C.smooth || BS.reduced() || BS.designMode() || !BS.fine()) return;
    if (typeof window.Lenis === 'undefined' || !gsap) return;
    const lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      allowNestedScroll: true,
      prevent: (node) => Boolean(node && node.closest && node.closest('dialog, [data-lenis-prevent], .shopify-section-group-overlay-group')),
    });
    BS.lenis = lenis;
    if (ST) lenis.on('scroll', ST.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
    /* Horizon locks the page with html[scroll-lock] while a drawer or modal is open */
    const sync = () => {
      const html = document.documentElement;
      if (html.hasAttribute('scroll-lock') || html.classList.contains('bs-is-loading')) lenis.stop();
      else lenis.start();
    };
    new MutationObserver(sync).observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock', 'class'] });
    sync();
    /* anchor links */
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href').length < 2) return;
      const target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target || target.closest('dialog')) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -100 });
    });
  };

  BS.scrollTo = (y, opts) => {
    if (BS.lenis) BS.lenis.scrollTo(y, Object.assign({ duration: 1.2 }, opts || {}));
    else window.scrollTo({ top: typeof y === 'number' ? y : y.getBoundingClientRect().top + window.scrollY, behavior: BS.reduced() ? 'auto' : 'smooth' });
  };

  /* ---------- reveals ---------- */
  BS.reveal = (root) => {
    const gsap = BS.gsap();
    const ST = BS.ST();
    const scope = root || document;
    if (!gsap || !ST || BS.reduced()) return;
    $$('.bs-display', scope).forEach((h) => {
      if (h.dataset.bsRevealed) return;
      const lines = $$('.bs-rv-line', h);
      if (!lines.length) return;
      h.dataset.bsRevealed = '1';
      gsap.fromTo(
        lines,
        { yPercent: 110 },
        { yPercent: 0, duration: 1.1, ease: 'expo.out', stagger: 0.08, scrollTrigger: { trigger: h, start: 'top 88%', once: true } }
      );
    });
    $$('.bs-num[data-to]', scope).forEach((n) => {
      if (n.dataset.bsCounted) return;
      n.dataset.bsCounted = '1';
      const to = Number(n.dataset.to) || 0;
      if (to < 2) return;
      const o = { v: 0 };
      ST.create({
        trigger: n,
        start: 'top 94%',
        once: true,
        onEnter: () => gsap.to(o, { v: to, duration: 1.6, ease: 'expo.out', onUpdate: () => { n.textContent = Math.round(o.v); } }),
      });
    });
    $$('[data-bs-fade]', scope).forEach((el) => {
      if (el.dataset.bsFaded) return;
      el.dataset.bsFaded = '1';
      gsap.fromTo(
        el.children.length && el.hasAttribute('data-bs-fade-children') ? el.children : el,
        { y: 34, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: 'expo.out', stagger: 0.07, clearProps: 'transform,opacity', scrollTrigger: { trigger: el, start: 'top 88%', once: true } }
      );
    });
  };

  /* ---------- centre-opening sections (T4 split) ---------- */
  BS.zips = [];
  BS.initZips = () => {
    const gsap = BS.gsap();
    const ST = BS.ST();
    BS.zips.forEach((t) => t.kill && t.kill());
    BS.zips = [];
    $$('[data-bs-zip]').forEach((el) => gsap && gsap.set(el, { clearProps: 'clipPath' }));
    if (!gsap || !ST || BS.reduced() || BS.designMode()) return;
    const desk = BS.desktop() && BS.fine();
    const groups = [];
    $$('[data-bs-zip]').forEach((el) => {
      const wrap = el.closest('.shopify-section') || el;
      const prev = wrap.previousElementSibling;
      const joinsPrev = el.dataset.bsZip === 'join' && prev && groups.length && groups[groups.length - 1].last === prev;
      if (joinsPrev) {
        const g = groups[groups.length - 1];
        g.els.push(el);
        g.last = wrap;
      } else {
        groups.push({ els: [el], last: wrap, dark: el.hasAttribute('data-bs-dark') });
      }
    });
    groups.forEach((g) => {
      const first = g.els[0];
      const inset = desk ? 50 : 30;
      const r = desk ? (g.dark ? 40 : 28) : 32;
      g.els.forEach((el, i) => {
        const radius = i === 0 ? r + 'px ' + r + 'px 0px 0px' : '0px 0px 0px 0px';
        const tween = gsap.fromTo(
          el,
          { clipPath: 'inset(0% ' + inset + '% 0% ' + inset + '% round ' + radius + ')' },
          {
            clipPath: 'inset(0% 0% 0% 0% round 0px 0px 0px 0px)',
            ease: 'none',
            scrollTrigger: { trigger: first, start: 'top bottom', end: desk ? (g.dark ? 'top 12%' : 'top top') : 'top 35%', scrub: true },
          }
        );
        BS.zips.push(tween.scrollTrigger || tween);
      });
    });
  };

  /* ---------- section dots ---------- */
  BS.initDots = () => {
    const nav = document.querySelector('[data-bs-dots]');
    if (!nav) return;
    const ST = BS.ST();
    const secs = $$('[data-bs-rail]');
    if (!C.pageDots || C.template !== 'index' || secs.length < 3 || BS.designMode()) {
      nav.hidden = true;
      return;
    }
    nav.hidden = false;
    nav.innerHTML = secs
      .map((s, i) => '<button type="button" data-p="' + i + '" aria-label="' + BS.esc(s.dataset.bsRail) + '"><i></i><span>' + BS.esc(s.dataset.bsRail) + '</span></button>')
      .join('');
    const set = (i) => {
      $$('button', nav).forEach((b, k) => {
        b.classList.toggle('is-on', k === i);
        if (k === i) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
      const s = secs[i];
      nav.classList.toggle('on-dark', Boolean(s && (s.dataset.bsSection === 'hero' || s.hasAttribute('data-bs-dark'))));
      heroOff = Boolean(s && s.dataset.bsSection === 'hero' && i === 0);
      sync();
    };
    let heroOff = false;
    let footOff = false;
    const sync = () => nav.classList.toggle('is-hidden', heroOff || footOff);
    set(0);
    /* step aside while the footer is on screen */
    const foot = document.querySelector('.bs-footer');
    if (foot && ST) {
      ST.create({ trigger: foot, start: 'top 75%', end: 'bottom top', onToggle: (self) => { footOff = self.isActive; sync(); } });
    } else if (foot && 'IntersectionObserver' in window) {
      new IntersectionObserver((en) => { footOff = en[0].isIntersecting; sync(); }, { rootMargin: '0px 0px -25% 0px' }).observe(foot);
    }
    nav.addEventListener('click', (e) => {
      const b = e.target.closest('[data-p]');
      if (!b) return;
      const s = secs[Number(b.dataset.p)];
      const wrap = s.closest('.shopify-section') || s;
      const y = wrap === document.querySelector('#MainContent > .shopify-section:first-child') ? 0 : wrap.getBoundingClientRect().top + window.scrollY;
      BS.scrollTo(y, { duration: 1.4 });
    });
    if (ST) {
      secs.forEach((s, i) => {
        ST.create({ trigger: s, start: 'top 55%', end: 'bottom 55%', onToggle: (self) => { if (self.isActive) set(i); } });
      });
    } else if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) set(secs.indexOf(en.target));
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      secs.forEach((s) => io.observe(s));
    }
  };
})();

/* ---- 40-hero.js ---- */
/* Hero: colourway swap. The jacket lifts out, the next colour flies in from the thumbnail, the background follows. */
(() => {
  'use strict';

  const BS = window.BStore;
  const C = BS.config;
  const { $, $$ } = BS;

  const readJSON = (el) => {
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  /* shared variant helpers for the hero and See it on */
  BS.variantFor = (data, colourValue, sizeValue) => {
    if (!data || !data.variants) return null;
    const ci = data.colourIndex;
    const si = data.sizeIndex;
    const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
    return (
      data.variants.find((v) => (ci < 0 || colourValue == null || norm(v.options[ci]) === norm(colourValue)) && (si < 0 || sizeValue == null || norm(v.options[si]) === norm(sizeValue))) || null
    );
  };

  BS.syncSizes = (chips, data, colourValue) => {
    chips.forEach((chip) => {
      const v = BS.variantFor(data, colourValue, chip.dataset.size);
      const ok = Boolean(v && v.available);
      chip.classList.toggle('is-off', !ok);
      chip.setAttribute('aria-disabled', ok ? 'false' : 'true');
    });
  };

  BS.register('hero', {
    init(root) {
      const gsap = BS.gsap();
      const ST = BS.ST();
      const data = readJSON($('[data-hero-json]', root)) || { variants: [], colours: [] };
      const jackets = $$('.bs-hero__jacket', root);
      const N = jackets.length;
      const floatEl = $('[data-hero-float]', root);
      const shadow = $('[data-hero-shadow]', root);
      const bgA = $('[data-bg-a]', root);
      const bgB = $('[data-bg-b]', root);
      const thumbImg = $('[data-hero-thumb]', root);
      const thumbName = $('[data-hero-thumb-name]', root);
      const ring = $('[data-hero-ring]', root);
      const nowEl = $('[data-hero-now]', root);
      const colourEl = $('[data-hero-colour]', root);
      const swatches = $$('[data-hc]', root);
      const sizeChips = $$('.bs-hero__sizes .bs-chip', root);
      const addBtns = $$('[data-hero-add]', root);
      const productPanels = $$('[data-hero-product]', root);
      const sizePanels = $$('[data-hero-sizes]', root);
      const panelEls = (i) => {
        const panel = productPanels[i] || root;
        return { price: $('[data-hero-price]', panel), was: $('[data-hero-was]', panel), saveRow: $('[data-hero-save-row]', panel), save: $('[data-hero-save]', panel) };
      };
      const autoplay = root.dataset.autoplay === 'true';
      const interval = Math.max(3, Number(root.dataset.interval) || 6);
      const fly = root.dataset.swap !== 'fade';
      const motion = Boolean(gsap) && !BS.reduced();
      const R = 50;
      const CIRC = 2 * Math.PI * R;
      const cleanups = [];
      const on = (el, ev, fn, opt) => {
        if (!el) return;
        el.addEventListener(ev, fn, opt);
        cleanups.push(() => el.removeEventListener(ev, fn, opt));
      };

      let cur = 0;
      let shown = 0;
      let busy = false;
      let started = false;
      let ringTween = null;
      let timer = null;
      let pinST = null;
      const tweens = [];
      const pauses = {};
      let size = (sizeChips.find((c) => c.classList.contains('is-on')) || {}).dataset?.size || null;

      const colour = (i) => data.colours[i] || { value: null, label: '', tone: [] };
      const tone = (layer, i) => {
        const t = colour(i).tone || [];
        if (!layer || !t.length) return;
        layer.style.setProperty('--g', t[0] || '#b98339');
        layer.style.setProperty('--m', t[1] || '#76491a');
        layer.style.setProperty('--e', t[2] || '#2a1808');
      };
      const srcOf = (i) => {
        const j = jackets[i];
        if (!j) return '';
        if (j.tagName === 'IMG') return j.currentSrc || j.src;
        const img = j.querySelector('img');
        return img ? img.currentSrc || img.src : '';
      };
      /* Per-product entries (new hero model). Falls back to the old
         single-product data when data.products is absent, so older
         hero instances keep working. Animation code below is untouched. */
      const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
      const entryFor = (i) => (data.products && data.products[i]) || null;
      const variantForEntry = (entry, sizeValue) => {
        if (!entry || !entry.variants || !entry.variants.length) return null;
        const cv = colour(shown).value;
        return (
          entry.variants.find((v) => (entry.colourIndex < 0 || norm(v.options[entry.colourIndex]) === norm(cv)) && (entry.sizeIndex < 0 || sizeValue == null || norm(v.options[entry.sizeIndex]) === norm(sizeValue))) || null
        );
      };

      if (ring) {
        ring.style.strokeDasharray = String(CIRC);
        ring.style.strokeDashoffset = String(CIRC);
      }

      /* ---------- price, sizes, add to cart ---------- */
      const variant = () => {
        const entry = entryFor(shown);
        return entry ? variantForEntry(entry, size) : BS.variantFor(data, colour(shown).value, size);
      };
      const renderPrice = () => {
        const entry = entryFor(shown);
        const els = panelEls(shown);
        const v = variant() || (entry ? variantForEntry(entry, null) : BS.variantFor(data, colour(shown).value, null));
        if (!v) {
          if (entry && entry.price) {
            if (els.price) els.price.textContent = BS.money(entry.price);
            if (els.was) {
              if (entry.compare > entry.price) {
                els.was.textContent = BS.money(entry.compare);
                els.was.hidden = false;
              } else els.was.hidden = true;
            }
            if (els.saveRow && els.save) {
              if (entry.compare > entry.price) {
                els.save.textContent = BS.money(entry.compare - entry.price);
                els.saveRow.hidden = false;
              } else els.saveRow.hidden = true;
            }
            addBtns.forEach((b) => {
              const label = $('[data-hero-add-label]', b);
              b.disabled = true;
              if (label) label.textContent = 'Coming soon';
            });
          }
          return;
        }
        if (els.price) els.price.textContent = BS.money(v.price);
        if (els.was) {
          if (v.compare > v.price) {
            els.was.textContent = BS.money(v.compare);
            els.was.hidden = false;
          } else els.was.hidden = true;
        }
        if (els.saveRow && els.save) {
          if (v.compare > v.price) {
            els.save.textContent = BS.money(v.compare - v.price);
            els.saveRow.hidden = false;
          } else els.saveRow.hidden = true;
        }
        const ok = Boolean(variant() && variant().available);
        addBtns.forEach((b) => {
          const label = $('[data-hero-add-label]', b);
          b.disabled = !ok && sizeChips.length > 0 && size != null;
          if (label) label.textContent = ok || !size ? data.ctaLabel || C.strings.addToCart : C.strings.soldOut;
        });
      };
      const renderSizes = () => {
        if (!sizePanels.length) {
          if (!sizeChips.length) return;
          BS.syncSizes(sizeChips, data, colour(shown).value);
          return;
        }
        const entry = entryFor(shown);
        sizePanels.forEach((panel) => { panel.hidden = String(panel.dataset.productI) !== String(shown); });
        const active = sizePanels.find((panel) => String(panel.dataset.productI) === String(shown));
        if (!active) return;
        $$('.bs-chip', active).forEach((chip) => {
          const v = entry ? variantForEntry(entry, chip.dataset.size) : null;
          const ok = Boolean(v && v.available);
          chip.classList.toggle('is-off', !ok);
          chip.setAttribute('aria-disabled', ok ? 'false' : 'true');
        });
      };

      sizeChips.forEach((chip) => {
        on(chip, 'click', () => {
          size = chip.dataset.size;
          const panel = chip.closest('[data-hero-sizes]');
          const scope = panel ? $$('.bs-chip', panel) : sizeChips;
          scope.forEach((x) => {
            x.classList.toggle('is-on', x === chip);
            x.setAttribute('aria-pressed', x === chip ? 'true' : 'false');
          });
          renderPrice();
        });
      });

      addBtns.forEach((b) => {
        on(b, 'click', async () => {
          const entry = entryFor(shown);
          if (entry && !entry.hasProduct) {
            BS.toast(C.strings.unavailable || 'Unavailable');
            return;
          }
          if (sizeChips.length && !size) {
            BS.toast(C.strings.chooseOption);
            return;
          }
          const v = variant();
          if (!v || !v.available) {
            BS.toast(C.strings.unavailable);
            return;
          }
          b.classList.add('is-loading');
          try {
            await BS.cart.add([{ id: v.id, quantity: 1 }], { source: b, productId: data.productId });
          } catch (err) {
            BS.toast(err.message || C.strings.error);
          } finally {
            b.classList.remove('is-loading');
          }
        });
      });

      /* ---------- UI state ---------- */
      const setUI = (i) => {
        shown = i;
        productPanels.forEach((panel, k) => { panel.hidden = k !== i; });
        const entry = entryFor(i);
        if (entry && entry.sizeValues && entry.sizeValues.length && size && !entry.sizeValues.includes(size)) size = null;
        const sizePanel = sizePanels.find((panel) => String(panel.dataset.productI) === String(i));
        if (sizePanel) {
          $$('.bs-chip', sizePanel).forEach((x) => {
            const on = Boolean(size && x.dataset.size === size);
            x.classList.toggle('is-on', on);
            x.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        }
        if (nowEl) nowEl.textContent = String(i + 1).padStart(2, '0');
        swatches.forEach((b, k) => {
          b.classList.toggle('is-on', k === i);
          b.setAttribute('aria-pressed', k === i ? 'true' : 'false');
        });
        if (colourEl) {
          if (motion) {
            tweens.push(
              gsap.fromTo(colourEl, { yPercent: 60, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.6, ease: 'expo.out', onStart: () => { colourEl.textContent = colour(i).label; } })
            );
          } else colourEl.textContent = colour(i).label;
        }
        renderSizes();
        renderPrice();
      };
      const setThumb = (i) => {
        if (thumbImg) thumbImg.src = srcOf(i);
        if (thumbName) thumbName.textContent = colour(i).label;
      };

      const measure = () => {
        const s = floatEl.getBoundingClientRect();
        const t = thumbImg ? thumbImg.getBoundingClientRect() : s;
        return {
          dx: t.left + t.width / 2 - (s.left + s.width / 2),
          dy: t.top + t.height / 2 - (s.top + s.height / 2),
          s: Math.max(0.12, t.height / (s.height || 1)),
        };
      };
      const exitPos = () => {
        const mobile = window.innerWidth < 900;
        return { x: -window.innerWidth * (mobile ? 0.12 : 0.15), y: -window.innerHeight * (mobile ? 0.5 : 0.64), scale: 0.42, rotation: -9 };
      };

      const crossBg = (i, dur) => {
        if (!bgB) return;
        tone(bgB, i);
        tweens.push(
          gsap.fromTo(bgB, { opacity: 0 }, { opacity: 1, duration: dur, ease: 'power2.inOut', onComplete: () => { tone(bgA, i); gsap.set(bgB, { opacity: 0 }); } })
        );
      };

      /* ---------- autoplay ---------- */
      const paused = () => Object.keys(pauses).some((k) => pauses[k]);
      const stopAuto = () => {
        if (ringTween) ringTween.kill();
        ringTween = null;
        clearTimeout(timer);
      };
      const startAuto = () => {
        stopAuto();
        if (!autoplay || N < 2 || BS.designMode()) return;
        if (gsap && ring) {
          ringTween = gsap.fromTo(ring, { strokeDashoffset: CIRC }, { strokeDashoffset: 0, duration: interval, ease: 'none', onComplete: () => go(cur + 1, 1) });
          if (paused()) ringTween.pause();
        } else if (!paused()) {
          timer = setTimeout(() => go(cur + 1, 1), interval * 1000);
        }
      };
      const pause = (k) => {
        pauses[k] = true;
        if (ringTween) ringTween.pause();
        clearTimeout(timer);
      };
      const resume = (k) => {
        pauses[k] = false;
        if (paused()) return;
        if (ringTween) ringTween.resume();
        else if (started) startAuto();
      };

      /* ---------- transitions ---------- */
      const finish = (target) => {
        cur = target;
        busy = false;
        startAuto();
      };

      const fade = (target) => {
        const prev = cur;
        jackets.forEach((j, k) => j.classList.toggle('is-on', k === target));
        if (gsap) {
          tweens.push(gsap.fromTo(jackets[target], { opacity: 0 }, { opacity: 1, duration: 0.6 }));
          tweens.push(gsap.to(jackets[prev], { opacity: 0, duration: 0.6 }));
          crossBg(target, 0.6);
        } else {
          tone(bgA, target);
          jackets.forEach((j, k) => { j.style.opacity = k === target ? 1 : 0; });
        }
        cur = target;
        setUI(target);
        setThumb((target + 1) % N);
        finish(target);
      };

      const forward = (target) => {
        const out = jackets[cur];
        const inn = jackets[target];
        const m = measure();
        const ex = exitPos();
        setThumb(target);
        gsap.set(inn, { x: m.dx, y: m.dy, scale: m.s, rotation: -8, opacity: 1, zIndex: 3 });
        gsap.set(out, { zIndex: 2 });
        if (thumbImg) gsap.set(thumbImg, { opacity: 0 });
        const tl = gsap.timeline({
          onComplete: () => {
            out.classList.remove('is-on');
            inn.classList.add('is-on');
            gsap.set(out, { opacity: 0, x: 0, y: 0, scale: 1, rotation: 0 });
            finish(target);
          },
        });
        tl.to(out, { x: ex.x, y: ex.y, scale: ex.scale, rotation: ex.rotation, opacity: 0, duration: 0.95, ease: 'power3.in' }, 0)
          .to(inn, { x: 0, y: 0, scale: 1, rotation: 0, duration: 1.1, ease: 'power3.out' }, 0.1)
          .fromTo(shadow, { scaleX: 0.45, opacity: 0.2 }, { scaleX: 1, opacity: 1, duration: 1, ease: 'expo.out' }, 0.35)
          .add(() => crossBg(target, 1), 0)
          .add(() => setUI(target), 0.25)
          .add(() => setThumb((target + 1) % N), 0.6);
        if (thumbImg) tl.fromTo(thumbImg, { opacity: 0, scale: 0.55, y: 26, rotation: 10 }, { opacity: 1, scale: 1, y: 0, rotation: 0, duration: 0.8, ease: 'expo.out' }, 0.62);
        tweens.push(tl);
      };

      const backward = (target) => {
        const out = jackets[cur];
        const inn = jackets[target];
        const m = measure();
        const ex = exitPos();
        if (thumbName) thumbName.textContent = colour(cur).label;
        gsap.set(inn, { x: ex.x, y: ex.y, scale: ex.scale, rotation: ex.rotation, opacity: 0, zIndex: 3 });
        gsap.set(out, { zIndex: 2 });
        const tl = gsap.timeline({
          onComplete: () => {
            out.classList.remove('is-on');
            inn.classList.add('is-on');
            gsap.set(out, { opacity: 0, x: 0, y: 0, scale: 1, rotation: 0 });
            setThumb((target + 1) % N);
            if (thumbImg) gsap.set(thumbImg, { opacity: 1, scale: 1, y: 0, rotation: 0 });
            finish(target);
          },
        });
        if (thumbImg) tl.to(thumbImg, { opacity: 0, scale: 0.6, duration: 0.3, ease: 'power2.in' }, 0);
        tl.to(inn, { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1, duration: 1.05, ease: 'power3.out' }, 0.06)
          .to(out, { x: m.dx, y: m.dy, scale: m.s, rotation: -8, duration: 0.95, ease: 'power3.inOut' }, 0)
          .fromTo(shadow, { scaleX: 0.45, opacity: 0.2 }, { scaleX: 1, opacity: 1, duration: 1, ease: 'expo.out' }, 0.3)
          .add(() => crossBg(target, 1), 0)
          .add(() => setUI(target), 0.25);
        tweens.push(tl);
      };

      function go(target, dir) {
        if (busy || !started || N < 2) return;
        target = (target + N) % N;
        if (target === cur) return;
        if (!dir) dir = target === (cur - 1 + N) % N ? -1 : 1;
        stopAuto();
        if (!motion || !fly) {
          fade(target);
          return;
        }
        busy = true;
        if (dir > 0) forward(target);
        else backward(target);
      }

      /* ---------- controls ---------- */
      on($('[data-hero-next]', root), 'click', () => go(cur + 1, 1));
      on($('[data-hero-prev]', root), 'click', () => go(cur - 1, -1));
      on($('[data-hero-next-thumb]', root), 'click', () => go(cur + 1, 1));
      swatches.forEach((b) => on(b, 'click', () => go(Number(b.dataset.hc))));
      $$('[data-hero-pause]', root).forEach((el) => {
        on(el, 'pointerenter', (e) => { if (e.pointerType === 'mouse') pause('hover'); });
        on(el, 'pointerleave', (e) => { if (e.pointerType === 'mouse') resume('hover'); });
        on(el, 'focusin', () => pause('focus'));
        on(el, 'focusout', () => resume('focus'));
      });

      let heroVisible = true;
      on(document, 'keydown', (e) => {
        if (!heroVisible || document.querySelector('dialog[open]')) return;
        if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
        if (e.key === 'ArrowRight') go(cur + 1, 1);
        if (e.key === 'ArrowLeft') go(cur - 1, -1);
      });

      let sx = null;
      let sy = null;
      on(floatEl, 'pointerdown', (e) => {
        sx = e.clientX;
        sy = e.clientY;
      });
      on(window, 'pointerup', (e) => {
        if (sx === null) return;
        const dx = e.clientX - sx;
        const dy = e.clientY - sy;
        sx = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(cur + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
      });

      let io = null;
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(
          (entries) => {
            const en = entries[0];
            heroVisible = en.isIntersecting && en.intersectionRatio > 0.3;
            if (heroVisible) resume('view');
            else pause('view');
          },
          { threshold: [0, 0.3, 0.6] }
        );
        io.observe(root);
      }
      const onVis = () => (document.hidden ? pause('tab') : resume('tab'));
      on(document, 'visibilitychange', onVis);
      const onDrawer = () => (document.documentElement.hasAttribute('scroll-lock') ? pause('drawer') : resume('drawer'));
      const mo = new MutationObserver(onDrawer);
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });

      /* ---------- pin: the next sections open over the held hero (desktop) ---------- */
      const setupPin = () => {
        if (pinST) {
          pinST.kill();
          pinST = null;
        }
        if (!ST || !gsap || root.dataset.pin !== 'true' || BS.designMode() || BS.reduced() || !BS.desktop() || !BS.fine()) return;
        const wrap = root.closest('.shopify-section');
        if (!wrap || !wrap.nextElementSibling) return;
        pinST = ST.create({ trigger: root, start: 'top top', end: '+=100%', pin: true, pinSpacing: false });
        tweens.push(
          gsap.to($$('.bs-hero__grid, .bs-hero__foot', root), {
            scale: 0.93,
            opacity: 0.35,
            ease: 'none',
            scrollTrigger: { trigger: wrap.nextElementSibling, start: 'top bottom', end: 'top top', scrub: true },
          })
        );
      };

      /* ---------- intro ---------- */
      const intro = () => {
        if (started) return;
        started = true;
        tone(bgA, 0);
        if (jackets[0]) jackets[0].classList.add('is-on');
        setThumb(N > 1 ? 1 : 0);
        renderSizes();
        renderPrice();
        if (!motion) {
          if (jackets[0]) jackets[0].style.opacity = 1;
          startAuto();
          return;
        }
        const tl = gsap.timeline({ onComplete: startAuto });
        if (jackets[0]) tl.fromTo(jackets[0], { y: window.innerHeight * 0.35, scale: 0.7, rotation: 6, opacity: 0 }, { y: 0, scale: 1, rotation: 0, opacity: 1, duration: 1.4, ease: 'expo.out' }, 0.15);
        if (shadow) tl.fromTo(shadow, { scaleX: 0.3, opacity: 0 }, { scaleX: 1, opacity: 1, duration: 1.2, ease: 'expo.out' }, 0.4);
        if (thumbImg) tl.fromTo(thumbImg, { opacity: 0, scale: 0.5, y: 30 }, { opacity: 1, scale: 1, y: 0, duration: 0.9, ease: 'expo.out' }, 0.9);
        tweens.push(tl);
        if (floatEl) tweens.push(gsap.to(floatEl, { y: -12, duration: 2.8, ease: 'sine.inOut', yoyo: true, repeat: -1 }));
        if (shadow) tweens.push(gsap.to(shadow, { scaleX: 0.9, opacity: 0.75, duration: 2.8, ease: 'sine.inOut', yoyo: true, repeat: -1 }));
      };

      /* text entrance used when the preloader hands over (or straight away) */
      const enter = () => {
        if (!motion) {
          intro();
          return;
        }
        const title = $('[data-hero-title]', root);
        let split = null;
        if (title && window.SplitText) {
          try {
            gsap.registerPlugin(window.SplitText);
            split = window.SplitText.create(title, { type: 'lines', mask: 'lines' });
          } catch (e) {
            split = null;
          }
        }
        const tl = gsap.timeline();
        tl.add(intro, 0);
        if (split && split.lines.length) tl.fromTo(split.lines, { yPercent: 110 }, { yPercent: 0, duration: 1.1, ease: 'expo.out', stagger: 0.08 }, 0.2);
        else if (title) tl.fromTo(title, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 1, ease: 'expo.out', clearProps: 'opacity,transform' }, 0.2);
        const bits = $$('.bs-hero__ctrl, .bs-hero__eyebrow, .bs-hero__sub, .bs-hero__ctas, .bs-hero__right > *, .bs-hero__socials, .bs-hero__tag', root);
        if (bits.length) tl.fromTo(bits, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 1, ease: 'expo.out', stagger: 0.05, clearProps: 'opacity,transform' }, 0.3);
        tweens.push(tl);
      };

      setupPin();
      if (document.getElementById('BsPreloader') && !BS.designMode()) {
        document.addEventListener('bstore:preloaded', enter, { once: true });
      } else {
        /* no preloader: let the brand fonts settle first so the headline splits on its real line breaks */
        const title = $('[data-hero-title]', root);
        if (motion && title) title.style.visibility = 'hidden';
        const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready.catch(() => {}) : Promise.resolve();
        Promise.race([fontsReady, new Promise((r) => setTimeout(r, 700))]).then(() => {
          if (title) title.style.visibility = '';
          enter();
        });
      }

      return {
        go,
        pause,
        resume,
        setupPin,
        select(i) {
          go(i);
        },
        destroy() {
          cleanups.forEach((fn) => fn());
          stopAuto();
          if (io) io.disconnect();
          mo.disconnect();
          if (pinST) pinST.kill(true);
          tweens.forEach((t) => {
            if (t && t.scrollTrigger) t.scrollTrigger.kill();
            if (t && t.kill) t.kill();
          });
        },
      };
    },
  });
})();

/* ---- 50-seeit.js ---- */
/* See it on: the flat photo splits down the middle to show the same colour worn. */
(() => {
  'use strict';

  const BS = window.BStore;
  const C = BS.config;
  const { $, $$, clamp } = BS;

  BS.register('seeit', {
    init(root) {
      const gsap = BS.gsap();
      const ST = BS.ST();
      let data = { variants: [], colours: [] };
      try {
        data = JSON.parse($('[data-seeit-json]', root).textContent);
      } catch (e) {
        /* keep defaults */
      }
      const halves = $$('.bs-split__half', root);
      const flats = $$('[data-split-flat]', root);
      const model = $('[data-split-model]', root);
      const tag = $('[data-split-tag]', root);
      const toggles = $$('.bs-split__toggle button', root);
      const swatches = $$('[data-sc]', root);
      const sizeChips = $$('.bs-seeit__sizes .bs-chip', root);
      const colourLabel = $('[data-seeit-colour]', root);
      const priceEl = $('[data-seeit-price]', root);
      const wasEl = $('[data-seeit-was]', root);
      const pctEl = $('[data-seeit-pct]', root);
      const addBtn = $('[data-seeit-add]', root);
      const tpls = $$('[data-seeit-colour-tpl]', root);
      const motion = Boolean(gsap) && !BS.reduced();
      const cleanups = [];
      const tweens = [];
      const on = (el, ev, fn) => {
        if (!el) return;
        el.addEventListener(ev, fn);
        cleanups.push(() => el.removeEventListener(ev, fn));
      };

      const state = { open: 0, colour: 0, manual: null };
      let size = (sizeChips.find((c) => c.classList.contains('is-on')) || {}).dataset?.size || null;
      let scrollOpen = 0;
      let pinST = null;

      const colour = (i) => data.colours[i] || { value: null, label: '' };
      const variant = () => BS.variantFor(data, colour(state.colour).value, size);

      const render = () => {
        const o = state.open;
        if (halves[0]) halves[0].style.transform = 'translate3d(' + -52 * o + '%,0,0)';
        if (halves[1]) halves[1].style.transform = 'translate3d(' + 52 * o + '%,0,0)';
        if (model) model.style.transform = 'scale(' + (1.08 - 0.08 * o) + ')';
        const worn = o > 0.5;
        if (tag) tag.textContent = worn ? $('[data-view="1"]', root)?.textContent || '' : $('[data-view="0"]', root)?.textContent || '';
        toggles.forEach((b) => {
          const isOn = (b.dataset.view === '1') === worn;
          b.classList.toggle('is-on', isOn);
          b.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        });
      };

      const animateTo = (v, d, ease, cb) => {
        if (!motion) {
          state.open = v;
          render();
          if (cb) cb();
          return;
        }
        tweens.push(gsap.to(state, { open: v, duration: d || 0.8, ease: ease || 'expo.out', onUpdate: render, onComplete: cb, overwrite: true }));
      };

      const copyImg = (from, to) => {
        if (!from || !to) return;
        if (from.getAttribute('srcset')) to.setAttribute('srcset', from.getAttribute('srcset'));
        if (from.getAttribute('sizes')) to.setAttribute('sizes', from.getAttribute('sizes'));
        to.src = from.getAttribute('src');
      };

      const load = (i) => {
        const tpl = tpls.find((t) => t.dataset.i === String(i));
        if (tpl) {
          const frag = tpl.content;
          const flatSrc = frag.querySelector('.bs-split__flat-src');
          const modelSrc = frag.querySelector('.bs-split__model-src');
          flats.forEach((f) => copyImg(flatSrc, f));
          if (model && modelSrc) {
            copyImg(modelSrc, model);
            model.alt = modelSrc.getAttribute('alt') || '';
          }
        }
        if (colourLabel) colourLabel.textContent = colour(i).label;
        swatches.forEach((b, k) => {
          b.classList.toggle('is-on', k === i);
          b.setAttribute('aria-pressed', k === i ? 'true' : 'false');
        });
        if (sizeChips.length) BS.syncSizes(sizeChips, data, colour(i).value);
        renderPrice();
      };

      const prefetch = (i) => {
        const tpl = tpls.find((t) => t.dataset.i === String(i));
        if (!tpl) return;
        $$('img', tpl.content).forEach((img) => {
          const im = new Image();
          if (img.getAttribute('srcset')) {
            im.sizes = img.getAttribute('sizes') || '50vw';
            im.srcset = img.getAttribute('srcset');
          }
          im.src = img.getAttribute('src');
        });
      };

      const renderPrice = () => {
        const v = variant() || BS.variantFor(data, colour(state.colour).value, null);
        if (!v) return;
        if (priceEl) priceEl.textContent = BS.money(v.price);
        if (wasEl) {
          wasEl.textContent = v.compare > v.price ? BS.money(v.compare) : '';
          wasEl.hidden = !(v.compare > v.price);
        }
        if (pctEl) {
          if (v.compare > v.price) {
            pctEl.textContent = (C.strings.save || 'Save') + ' ' + Math.round((1 - v.price / v.compare) * 100) + '%';
            pctEl.hidden = false;
          } else pctEl.hidden = true;
        }
        if (addBtn) {
          const ok = Boolean(variant() && variant().available);
          const label = $('[data-seeit-add-label]', addBtn);
          addBtn.disabled = !ok && size != null && sizeChips.length > 0;
          if (label) label.textContent = ok || !size ? data.ctaLabel || C.strings.addToCart : C.strings.soldOut;
        }
      };

      const wanted = () => (state.manual === null ? scrollOpen : state.manual);

      const setColour = (i) => {
        if (i === state.colour) return;
        state.colour = i;
        prefetch(i);
        const back = wanted();
        if (state.open < 0.05) {
          load(i);
          return;
        }
        animateTo(0, 0.45, 'power3.inOut', () => {
          load(i);
          setTimeout(() => animateTo(Math.max(back, 0.999), 0.9, 'expo.out'), 120);
        });
      };

      swatches.forEach((b) => {
        on(b, 'click', () => {
          state.manual = 1;
          setColour(Number(b.dataset.sc));
        });
        on(b, 'pointerenter', () => prefetch(Number(b.dataset.sc)));
      });
      toggles.forEach((b) => {
        on(b, 'click', () => {
          state.manual = Number(b.dataset.view);
          animateTo(state.manual, 0.9, 'expo.out');
        });
      });
      sizeChips.forEach((chip) => {
        on(chip, 'click', () => {
          size = chip.dataset.size;
          sizeChips.forEach((x) => {
            x.classList.toggle('is-on', x === chip);
            x.setAttribute('aria-pressed', x === chip ? 'true' : 'false');
          });
          renderPrice();
        });
      });
      on(addBtn, 'click', async () => {
        if (sizeChips.length && !size) {
          BS.toast(C.strings.chooseOption);
          return;
        }
        const v = variant();
        if (!v || !v.available) {
          BS.toast(C.strings.unavailable);
          return;
        }
        addBtn.classList.add('is-loading');
        try {
          await BS.cart.add([{ id: v.id, quantity: 1 }], { source: addBtn, productId: data.productId });
        } catch (err) {
          BS.toast(err.message || C.strings.error);
        } finally {
          addBtn.classList.remove('is-loading');
        }
      });

      if (sizeChips.length) BS.syncSizes(sizeChips, data, colour(0).value);
      renderPrice();
      render();

      /* ---------- scroll: pinned on desktop, opens once on touch ---------- */
      let io = null;
      const openOnce = () => {
        if (state.open < 0.5) animateTo(1, 1.2, 'expo.out');
      };
      const desk = Boolean(ST) && motion && BS.desktop() && BS.fine() && !BS.designMode() && root.dataset.pin === 'true';
      if (desk) {
        pinST = ST.create({
          trigger: root,
          start: 'top top',
          end: '+=90%',
          pin: true,
          onUpdate: (self) => {
            scrollOpen = clamp((self.progress - 0.12) / 0.55, 0, 1);
            if (state.manual === null) {
              state.open = scrollOpen;
              render();
            }
          },
          onEnter: () => { state.manual = null; },
          onEnterBack: () => { state.manual = null; },
        });
        const info = $$('.bs-seeit__info > *, .bs-seeit__buy > *', root);
        if (info.length) tweens.push(gsap.fromTo(info, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 1, ease: 'expo.out', stagger: 0.06, clearProps: 'transform,opacity', scrollTrigger: { trigger: root, start: 'top 70%', once: true } }));
        const split = $('[data-split]', root);
        if (split) tweens.push(gsap.fromTo(split, { scale: 0.9, opacity: 0 }, { scale: 1, opacity: 1, duration: 1.2, ease: 'expo.out', clearProps: 'transform,opacity', scrollTrigger: { trigger: root, start: 'top 70%', once: true } }));
      } else if ('IntersectionObserver' in window && !BS.designMode()) {
        io = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              setTimeout(openOnce, 350);
              io.disconnect();
            }
          },
          { threshold: 0.55 }
        );
        const split = $('[data-split]', root);
        if (split) io.observe(split);
      } else {
        openOnce();
      }

      return {
        select(i) {
          state.manual = 1;
          setColour(i);
          openOnce();
        },
        destroy() {
          cleanups.forEach((fn) => fn());
          if (io) io.disconnect();
          if (pinST) pinST.kill(true);
          tweens.forEach((t) => {
            if (t && t.scrollTrigger) t.scrollTrigger.kill();
            if (t && t.kill) t.kill();
          });
        },
      };
    },
  });
})();

/* ---- 60-sections.js ---- */
/* Categories (depth tilt), product rail, pay ahead band, visit, footer, trust. */
(() => {
  'use strict';

  const BS = window.BStore;
  const { $, $$, clamp } = BS;

  const killAll = (list) =>
    list.forEach((t) => {
      if (t && t.scrollTrigger) t.scrollTrigger.kill();
      if (t && t.kill) t.kill();
    });

  /* ---------- categories ---------- */
  BS.register('categories', {
    init(root) {
      const gsap = BS.gsap();
      const cleanups = [];
      const tweens = [];
      if (gsap && !BS.reduced() && BS.ST()) {
        const cards = $$('.bs-cat', root);
        if (cards.length && !BS.designMode()) {
          tweens.push(
            gsap.fromTo(cards, { y: BS.desktop() ? 80 : 40, opacity: 0 }, { y: 0, opacity: 1, duration: 1.1, ease: 'expo.out', stagger: 0.08, clearProps: 'transform,opacity', scrollTrigger: { trigger: $('.bs-cat-row', root), start: 'top 90%', once: true } })
          );
        }
      }
      if (gsap && BS.fine() && !BS.reduced() && root.dataset.tilt === 'true') {
        $$('.bs-cat', root).forEach((card) => {
          const tilt = $('.bs-cat__tilt', card);
          const glare = $('.bs-cat__glare', card);
          const rX = gsap.quickTo(tilt, 'rotationX', { duration: 0.7, ease: 'power3.out' });
          const rY = gsap.quickTo(tilt, 'rotationY', { duration: 0.7, ease: 'power3.out' });
          const move = (e) => {
            const r = card.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width;
            const py = (e.clientY - r.top) / r.height;
            rX((0.5 - py) * 12);
            rY((px - 0.5) * 14);
            glare.style.setProperty('--gx', px * 100 + '%');
            glare.style.setProperty('--gy', py * 100 + '%');
          };
          const leave = () => {
            rX(0);
            rY(0);
          };
          card.addEventListener('pointermove', move);
          card.addEventListener('pointerleave', leave);
          cleanups.push(() => {
            card.removeEventListener('pointermove', move);
            card.removeEventListener('pointerleave', leave);
            gsap.set(tilt, { clearProps: 'transform' });
          });
        });
      }
      BS.reveal(root);
      return {
        destroy() {
          cleanups.forEach((fn) => fn());
          killAll(tweens);
        },
      };
    },
  });

  /* ---------- trust ---------- */
  BS.register('trust', {
    init(root) {
      const gsap = BS.gsap();
      const tweens = [];
      if (gsap && BS.ST() && !BS.reduced() && !BS.designMode()) {
        const items = $$('.bs-trust__item', root);
        if (items.length) tweens.push(gsap.fromTo(items, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, ease: 'expo.out', stagger: 0.06, clearProps: 'transform,opacity', scrollTrigger: { trigger: root, start: 'top 92%', once: true } }));
      }
      return { destroy: () => killAll(tweens) };
    },
  });

  /* ---------- product rail ---------- */
  BS.register('rail', {
    init(root) {
      const gsap = BS.gsap();
      const ST = BS.ST();
      const rail = $('[data-rail]', root);
      const bar = $('[data-rail-bar]', root);
      const now = $('[data-rail-now]', root);
      const prev = $('[data-rail-prev]', root);
      const next = $('[data-rail-next]', root);
      const cards = $$('.bs-rail__card', root);
      const cleanups = [];
      const tweens = [];
      const on = (el, ev, fn, opt) => {
        if (!el) return;
        el.addEventListener(ev, fn, opt);
        cleanups.push(() => el.removeEventListener(ev, fn, opt));
      };
      const setProgress = (p) => {
        if (bar) bar.style.transform = 'scaleX(' + (0.08 + 0.92 * clamp(p, 0, 1)) + ')';
        if (now) now.textContent = String(Math.min(cards.length, Math.round(p * Math.max(0, cards.length - 1)) + 1)).padStart(2, '0');
      };
      BS.reveal(root);

      const pinned = Boolean(gsap && ST) && !BS.reduced() && !BS.designMode() && BS.desktop() && BS.fine() && root.dataset.pin === 'true';

      if (pinned) {
        root.classList.add('is-pinned');
        const dist = () => Math.max(0, rail.scrollWidth - window.innerWidth);
        const railTween = gsap.to(rail, {
          x: () => -dist(),
          ease: 'none',
          scrollTrigger: {
            trigger: root,
            start: 'top top',
            end: () => '+=' + dist(),
            pin: true,
            scrub: 0.8,
            invalidateOnRefresh: true,
            onUpdate: (self) => setProgress(self.progress),
          },
        });
        tweens.push(railTween);
        $$('.bs-card__par', rail).forEach((el) => {
          tweens.push(
            gsap.fromTo(el, { xPercent: -5 }, { xPercent: 5, ease: 'none', scrollTrigger: { trigger: el.closest('.bs-rail__card'), containerAnimation: railTween, start: 'left right', end: 'right left', scrub: true } })
          );
        });
        const step = () => (cards[0] ? cards[0].offsetWidth + 20 : 360);
        const by = (d) => {
          const st = railTween.scrollTrigger;
          const y = clamp(window.scrollY + d * step(), st.start, st.end);
          BS.scrollTo(y, { duration: 1 });
        };
        on(prev, 'click', () => by(-1));
        on(next, 'click', () => by(1));
        /* drag the row with the mouse */
        let drag = null;
        on(rail, 'pointerdown', (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button, a.bs-link-arrow')) return;
          drag = { x: e.clientX, y: window.scrollY, moved: false };
        });
        on(window, 'pointermove', (e) => {
          if (!drag) return;
          const dx = e.clientX - drag.x;
          if (Math.abs(dx) > 4) drag.moved = true;
          const st = railTween.scrollTrigger;
          const y = clamp(drag.y - dx * 1.2, st.start, st.end);
          if (BS.lenis) BS.lenis.scrollTo(y, { immediate: true });
          else window.scrollTo(0, y);
        });
        on(window, 'pointerup', () => {
          if (drag && drag.moved) {
            const stop = (ev) => {
              ev.stopPropagation();
              ev.preventDefault();
              window.removeEventListener('click', stop, true);
            };
            window.addEventListener('click', stop, true);
            setTimeout(() => window.removeEventListener('click', stop, true), 60);
          }
          drag = null;
        });
        on(rail, 'dragstart', (e) => e.preventDefault());
      } else {
        root.classList.remove('is-pinned');
        const onScroll = () => {
          const max = rail.scrollWidth - rail.clientWidth;
          setProgress(max > 0 ? rail.scrollLeft / max : 0);
        };
        const by = (d) => rail.scrollBy({ left: d * (cards[0] ? cards[0].offsetWidth + 20 : 300), behavior: BS.reduced() ? 'auto' : 'smooth' });
        on(rail, 'scroll', onScroll, { passive: true });
        on(prev, 'click', () => by(-1));
        on(next, 'click', () => by(1));
        onScroll();
        if (gsap && ST && !BS.reduced() && cards.length && !BS.designMode()) {
          tweens.push(gsap.fromTo(cards.slice(0, 5), { x: 60, opacity: 0 }, { x: 0, opacity: 1, duration: 1, ease: 'expo.out', stagger: 0.07, clearProps: 'transform,opacity', scrollTrigger: { trigger: rail, start: 'top 88%', once: true } }));
        }
      }

      return {
        destroy() {
          cleanups.forEach((fn) => fn());
          killAll(tweens);
          root.classList.remove('is-pinned');
          if (gsap) gsap.set(rail, { clearProps: 'transform' });
        },
      };
    },
  });

  /* ---------- pay ahead band: odometer and floating products ---------- */
  const buildOdo = (el) => {
    const to = el.dataset.to || el.textContent.trim();
    let from = el.dataset.from || to;
    const firstDigit = to.search(/\d/);
    if (firstDigit < 0) return null;
    const prefix = to.slice(0, firstDigit);
    const body = to.slice(firstDigit).replace(/\s+$/, '');
    let fromBody = from.slice(Math.max(0, from.search(/\d/)));
    const digitsTo = body.replace(/\D/g, '');
    let digitsFrom = fromBody.replace(/\D/g, '');
    if (digitsFrom.length !== digitsTo.length) digitsFrom = digitsTo.replace(/\d/g, '0');
    let html = prefix ? '<span class="bs-odo__cur">' + BS.esc(prefix.trim()) + '</span>' : '';
    let di = 0;
    for (let i = 0; i < body.length; i++) {
      const ch = body[i];
      if (/\d/.test(ch)) {
        html += '<span class="bs-odo__col" data-from="' + digitsFrom[di] + '" data-to="' + ch + '">';
        for (let k = 0; k < 20; k++) html += '<span>' + (k % 10) + '</span>';
        html += '</span>';
        di++;
      } else {
        html += '<span>' + BS.esc(ch) + '</span>';
      }
    }
    el.innerHTML = html;
    return $$('.bs-odo__col', el);
  };

  BS.register('payahead', {
    init(root) {
      const gsap = BS.gsap();
      const ST = BS.ST();
      const tweens = [];
      const odo = $('[data-odo]', root);
      const was = $('[data-calc-was]', root);
      BS.reveal(root);
      if (odo && gsap) {
        const cols = buildOdo(odo) || [];
        const endIdx = (c) => {
          const f = Number(c.dataset.from);
          const t = Number(c.dataset.to);
          return t < f ? t + 10 : t;
        };
        /* the final number is what shows by default */
        cols.forEach((c) => gsap.set(c, { yPercent: -endIdx(c) * 5 }));
        if (!BS.reduced() && ST && !BS.designMode()) {
          let played = false;
          const play = () => {
            if (played) return;
            played = true;
            const tl = gsap.timeline();
            cols.forEach((c) => gsap.set(c, { yPercent: -Number(c.dataset.from) * 5 }));
            if (was) tl.fromTo(was, { '--strike': 0 }, { '--strike': 1, duration: 0.7, ease: 'expo.out' }, 0.1);
            cols.forEach((c, i) => tl.to(c, { yPercent: -endIdx(c) * 5, duration: 1.4 + i * 0.12, ease: 'expo.out' }, 0.45));
            tweens.push(tl);
          };
          const calc = $('[data-calc]', root);
          tweens.push(ST.create({ trigger: calc || odo, start: 'top 78%', once: true, onEnter: play }));
        }
      }
      if (gsap && ST && !BS.reduced() && !BS.designMode()) {
        $$('[data-float-zone] [data-speed]', root).forEach((el) => {
          const sp = Number(el.dataset.speed) || 0;
          tweens.push(gsap.fromTo(el, { yPercent: 40 * sp + 30 }, { yPercent: -60 * sp - 20, ease: 'none', scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true } }));
        });
        $$('.bs-floater img', root).forEach((im, i) => {
          tweens.push(gsap.to(im, { rotation: i % 2 ? 4 : -4, y: i % 2 ? 10 : -10, duration: 3 + i * 0.4, ease: 'sine.inOut', yoyo: true, repeat: -1 }));
        });
      }
      return { destroy: () => killAll(tweens) };
    },
  });

  /* ---------- visit ---------- */
  BS.register('visit', {
    init(root) {
      const gsap = BS.gsap();
      const tweens = [];
      BS.reveal(root);
      if (gsap && BS.ST() && !BS.reduced() && !BS.designMode()) {
        const items = $$('.bs-visit__list li, .bs-visit__ctas', root);
        if (items.length) tweens.push(gsap.fromTo(items, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 1, ease: 'expo.out', stagger: 0.07, clearProps: 'transform,opacity', scrollTrigger: { trigger: items[0], start: 'top 88%', once: true } }));
        const news = $('.bs-news', root);
        if (news) tweens.push(gsap.fromTo(news, { y: 60, opacity: 0 }, { y: 0, opacity: 1, duration: 1.2, ease: 'expo.out', clearProps: 'transform,opacity', scrollTrigger: { trigger: news, start: 'top 90%', once: true } }));
      }
      return { destroy: () => killAll(tweens) };
    },
  });

  /* ---------- footer ---------- */
  BS.register('footer', {
    init(root) {
      const gsap = BS.gsap();
      const tweens = [];
      const big = $('[data-footer-big]', root);
      if (big && gsap && BS.ST() && !BS.reduced()) {
        tweens.push(gsap.fromTo(big, { xPercent: 6 }, { xPercent: -6, ease: 'none', scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom bottom', scrub: true } }));
      }
      return { destroy: () => killAll(tweens) };
    },
  });

  /* ---------- generic: inner page heroes and content sections ---------- */
  BS.register('reveal', {
    init(root) {
      BS.reveal(root);
      return {};
    },
  });
})();

/* ---- 70-quickview.js ---- */
/* Quick view: product gallery, options and Add to Cart in a dialog, filled from /products/<handle>.js */
(() => {
  'use strict';

  const BS = window.BStore;
  const C = BS.config;
  const { $, $$ } = BS;

  const cache = new Map();
  const key = (src) => String(src || '').split('?')[0].replace(/^https?:/, '');
  const isColour = (name) => /colou?rs?\b/i.test(name || '');

  const imgUrl = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? 'https:' + src : src;
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  };

  const NAMED = {
    black: '#171717', white: '#f3f3f1', navy: '#1f2b4d', red: '#c4161f', maroon: '#6d1424', burgundy: '#6d1424',
    grey: '#b9b9b7', gray: '#b9b9b7', charcoal: '#3d3d3f', golden: '#c7a265', gold: '#c7a265', beige: '#d9c7a7',
    brown: '#5a3b2a', blue: '#2f55a4', 'sky blue': '#a9c6e3', green: '#1f4d33', 'dark green': '#1f4d33',
    'forest green': '#1d4a33', olive: '#6b6b3a', pink: '#e9a7b8', purple: '#8e72b8', yellow: '#f2cf3a', orange: '#d9692b',
  };
  const colourOf = (name) => {
    const n = String(name || '').trim().toLowerCase();
    if (NAMED[n]) return NAMED[n];
    const key = Object.keys(NAMED).find((k) => n.includes(k));
    return key ? NAMED[key] : '#cfc7bb';
  };

  const load = async (handle) => {
    if (cache.has(handle)) return cache.get(handle);
    const p = fetch(C.routes.products + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } }).then((r) => {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
    cache.set(handle, p);
    p.catch(() => cache.delete(handle));
    return p;
  };

  const descriptionBits = (html) => {
    const box = document.createElement('div');
    box.innerHTML = html || '';
    $$('script, style, img, iframe', box).forEach((n) => n.remove());
    const items = $$('li', box)
      .map((li) => li.textContent.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .slice(0, 4);
    if (items.length >= 2) return '<ul>' + items.map((t) => '<li>' + BS.esc(t) + '</li>').join('') + '</ul>';
    const text = box.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return '';
    const short = text.length > 220 ? text.slice(0, 220).replace(/\s+\S*$/, '') + '...' : text;
    return '<p>' + BS.esc(short) + '</p>';
  };

  const init = () => {
    const dlg = document.querySelector('[data-bs-qv-dialog]');
    if (!dlg) return;
    const gal = $('[data-qv-gal]', dlg);
    const title = $('[data-qv-title]', dlg);
    const price = $('[data-qv-price]', dlg);
    const opts = $('[data-qv-options]', dlg);
    const desc = $('[data-qv-desc]', dlg);
    const addBtn = $('[data-qv-add]', dlg);
    const addLabel = $('[data-qv-add-label]', dlg);
    const link = $('[data-qv-link]', dlg);
    const errorEl = $('[data-qv-error]', dlg);
    let product = null;
    let selected = [];
    let opener = null;
    let closeTimer = null;

    const current = () => {
      if (!product) return null;
      return product.variants.find((v) => v.options.every((val, i) => selected[i] == null || val === selected[i])) || null;
    };

    const setError = (msg) => {
      if (!errorEl) return;
      errorEl.textContent = msg || '';
      errorEl.hidden = !msg;
    };

    const renderPrice = () => {
      const v = current() || product.variants[0];
      let html = BS.esc(BS.money(v.price));
      if (v.compare_at_price && v.compare_at_price > v.price) {
        html += '<s>' + BS.esc(BS.money(v.compare_at_price)) + '</s>';
        html += '<small>' + BS.esc((C.strings.save || 'Save') + ' ' + Math.round((1 - v.price / v.compare_at_price) * 100) + '%') + '</small>';
      }
      price.innerHTML = html;
    };

    const renderAvailability = () => {
      product.options.forEach((opt, oi) => {
        $$('[data-o="' + oi + '"]', opts).forEach((btn) => {
          const value = btn.dataset.v;
          const test = selected.slice();
          test[oi] = value;
          const ok = product.variants.some((v) => v.available && v.options.every((val, i) => test[i] == null || val === test[i]));
          btn.classList.toggle('is-off', !ok);
        });
      });
      const v = current();
      const complete = selected.every((s) => s != null);
      if (!complete) {
        addBtn.disabled = false;
        addLabel.textContent = C.strings.addToCart;
      } else if (v && v.available) {
        addBtn.disabled = false;
        addLabel.textContent = C.strings.addToCart;
      } else {
        addBtn.disabled = true;
        addLabel.textContent = v ? C.strings.soldOut : C.strings.unavailable;
      }
    };

    const showImage = (src) => {
      if (!src) return;
      const imgs = $$(':scope > img', gal);
      const target = imgs.find((im) => key(im.dataset.src) === key(src));
      if (target) {
        imgs.forEach((im) => im.classList.toggle('is-on', im === target));
        $$('.bs-qv__thumbs button', gal).forEach((b) => b.classList.toggle('is-on', key(b.dataset.src) === key(src)));
      }
    };

    const renderGallery = () => {
      const images = (product.images || []).slice(0, 8);
      if (!images.length) {
        gal.innerHTML = '';
        return;
      }
      const alt = BS.esc(product.title);
      gal.innerHTML =
        images
          .map((src, i) => '<img src="' + imgUrl(src, 1000) + '" srcset="' + imgUrl(src, 600) + ' 600w, ' + imgUrl(src, 1000) + ' 1000w, ' + imgUrl(src, 1400) + ' 1400w" sizes="(min-width: 900px) 520px, 100vw" data-src="' + BS.esc(src) + '" alt="' + (i === 0 ? alt : alt + ', ' + (i + 1)) + '" class="' + (i === 0 ? 'is-on' : '') + '"' + (i > 0 ? ' loading="lazy"' : '') + '>')
          .join('') +
        (images.length > 1
          ? '<div class="bs-qv__thumbs">' +
            images.map((src, i) => '<button type="button" class="' + (i === 0 ? 'is-on' : '') + '" data-src="' + BS.esc(src) + '" aria-label="' + BS.esc(C.strings.imageOf || 'Image') + ' ' + (i + 1) + '"><img src="' + imgUrl(src, 160) + '" alt="" loading="lazy"></button>').join('') +
            '</div>'
          : '');
    };

    const renderOptions = () => {
      if (product.variants.length === 1 && /default title/i.test(product.variants[0].title)) {
        opts.innerHTML = '';
        return;
      }
      opts.innerHTML = product.options
        .map((opt, oi) => {
          const colour = isColour(opt.name);
          const head = '<p class="bs-opt-label">' + BS.esc(opt.name) + (colour ? ': <b data-qv-chosen="' + oi + '">' + BS.esc(selected[oi] || '') + '</b>' : '') + '</p>';
          const values = opt.values
            .map((val) => {
              const on = selected[oi] === val;
              if (colour) {
                return '<button type="button" class="bs-sw' + (on ? ' is-on' : '') + '" style="--c:' + colourOf(val) + '" data-o="' + oi + '" data-v="' + BS.esc(val) + '" aria-label="' + BS.esc(val) + '" aria-pressed="' + on + '"></button>';
              }
              return '<button type="button" class="bs-chip' + (on ? ' is-on' : '') + '" data-o="' + oi + '" data-v="' + BS.esc(val) + '" aria-pressed="' + on + '">' + BS.esc(val) + '</button>';
            })
            .join('');
          return '<div class="bs-qv__opt" role="group" aria-label="' + BS.esc(opt.name) + '">' + head + '<div class="bs-qv__values">' + values + '</div></div>';
        })
        .join('');
    };

    const open = async (handle, trigger, focusOptions) => {
      opener = trigger || document.activeElement;
      clearTimeout(closeTimer);
      setError('');
      dlg.classList.add('is-loading');
      if (!dlg.open) {
        dlg.showModal();
        BS.lock(dlg);
        requestAnimationFrame(() => dlg.classList.add('is-open'));
      }
      try {
        product = await load(handle);
      } catch (err) {
        dlg.classList.remove('is-loading');
        close();
        window.location.href = C.routes.products + handle;
        return;
      }
      /* preselect: the first available variant, but leave a multi-value size open when asked to choose */
      const first = product.variants.find((v) => v.available) || product.variants[0];
      selected = product.options.map((opt, i) => (opt.values.length === 1 ? opt.values[0] : isColour(opt.name) ? first.options[i] : focusOptions ? null : first.options[i]));
      title.textContent = product.title;
      link.href = product.url || C.routes.products + handle;
      desc.innerHTML = descriptionBits(product.description);
      renderGallery();
      renderOptions();
      renderPrice();
      renderAvailability();
      dlg.classList.remove('is-loading');
      const v = current();
      if (v && v.featured_image) showImage(v.featured_image.src);
      const focusTarget = focusOptions ? $('.bs-qv__values button:not(.is-off)', opts) || addBtn : $('[data-qv-close]', dlg);
      if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 80);
    };

    const close = () => {
      if (!dlg.open) return;
      dlg.classList.remove('is-open');
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        dlg.close();
      }, BS.reduced() ? 0 : 380);
    };

    dlg.addEventListener('close', () => {
      BS.unlock(dlg);
      dlg.classList.remove('is-open');
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
      dlg.dispatchEvent(new CustomEvent('bstore:qv-closed', { bubbles: true }));
    });
    dlg.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg) {
        close();
        return;
      }
      if (e.target.closest('[data-qv-close]')) {
        close();
        return;
      }
      const thumb = e.target.closest('.bs-qv__thumbs button');
      if (thumb) {
        showImage(thumb.dataset.src);
        return;
      }
      const val = e.target.closest('[data-o]');
      if (val && product) {
        const oi = Number(val.dataset.o);
        selected[oi] = val.dataset.v;
        $$('[data-o="' + oi + '"]', opts).forEach((b) => {
          const isOn = b === val;
          b.classList.toggle('is-on', isOn);
          b.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        });
        const chosen = $('[data-qv-chosen="' + oi + '"]', opts);
        if (chosen) chosen.textContent = val.dataset.v;
        setError('');
        renderPrice();
        renderAvailability();
        const v = current();
        if (v && v.featured_image) showImage(v.featured_image.src);
      }
    });

    addBtn.addEventListener('click', async () => {
      if (!product) return;
      const missing = product.options.findIndex((o, i) => selected[i] == null);
      if (missing >= 0) {
        setError((C.strings.chooseOption || '') + ': ' + product.options[missing].name);
        const first = $('[data-o="' + missing + '"]:not(.is-off)', opts);
        if (first) first.focus();
        return;
      }
      const v = current();
      if (!v || !v.available) {
        setError(C.strings.unavailable);
        return;
      }
      addBtn.classList.add('is-loading');
      addLabel.textContent = C.strings.adding || C.strings.addToCart;
      try {
        await BS.cart.add([{ id: v.id, quantity: 1 }], { source: addBtn, context: 'dialog', productId: product.id, open: false });
        dlg.addEventListener('close', () => BS.cart.open(), { once: true });
        close();
      } catch (err) {
        setError(err.message || C.strings.error);
      } finally {
        addBtn.classList.remove('is-loading');
        renderAvailability();
      }
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-bs-qv]');
      if (!btn) return;
      e.preventDefault();
      /* a card button inside the open cart drawer: close the drawer first */
      const drawerEl = btn.closest('theme-drawer');
      if (drawerEl && typeof drawerEl.close === 'function') drawerEl.close();
      open(btn.dataset.bsQv, btn, btn.dataset.qvFocus === 'options');
    });

    /* warm the cache when a card is hovered */
    document.addEventListener(
      'pointerover',
      (e) => {
        const btn = e.target instanceof Element ? e.target.closest('[data-bs-qv]') : null;
        if (btn && !cache.has(btn.dataset.bsQv)) load(btn.dataset.bsQv).catch(() => {});
      },
      { passive: true }
    );

    BS.quickView = { open, close };
  };

  BS.initQuickView = init;
})();

/* ---- 80-product.js ---- */
/* Product page helpers: same-day dispatch countdown and the size guide dialog. */
(() => {
  'use strict';

  const BS = window.BStore;
  const C = BS.config;
  const { $, $$ } = BS;

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const karachiNow = () => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Karachi', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(new Date());
      const get = (t) => (parts.find((p) => p.type === t) || {}).value;
      return { day: DAYS.indexOf(get('weekday')), hour: Number(get('hour')), minute: Number(get('minute')) };
    } catch (e) {
      const d = new Date(Date.now() + (5 * 60 + new Date().getTimezoneOffset()) * 60000);
      return { day: d.getDay(), hour: d.getHours(), minute: d.getMinutes() };
    }
  };

  const dayName = (i) => {
    try {
      const base = new Date(Date.UTC(2026, 0, 4 + i));
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en', { weekday: 'long', timeZone: 'UTC' }).format(base);
    } catch (e) {
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][i];
    }
  };

  const countdowns = () => {
    if (!$('[data-bs-countdown]')) return;
    const tick = () => {
      const now = karachiNow();
      $$('[data-bs-countdown]').forEach((el) => {
        const cutoff = Number(el.dataset.cutoff || C.cutoffHour || 12);
        const out = $('[data-countdown-text]', el) || el;
        const workingDay = now.day !== 0;
        if (workingDay && now.hour < cutoff) {
          const mins = (cutoff - now.hour) * 60 - now.minute;
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          const time = (h ? h + 'h ' : '') + m + 'm';
          out.textContent = BS.t('dispatchToday', { time });
        } else {
          let next = (now.day + 1) % 7;
          if (next === 0) next = 1;
          out.textContent = BS.t('dispatchNext', { day: dayName(next) });
        }
      });
    };
    tick();
    setInterval(tick, 30000);
  };

  const sizeGuides = () => {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-bs-size-guide-open]');
      if (opener) {
        const dlg = document.getElementById(opener.getAttribute('aria-controls'));
        if (!dlg) return;
        e.preventDefault();
        dlg.showModal();
        BS.lock(dlg);
        requestAnimationFrame(() => dlg.classList.add('is-open'));
        return;
      }
      const dlg = e.target.closest('dialog[data-bs-size-guide]');
      if (dlg && (e.target === dlg || e.target.closest('[data-size-guide-close]'))) {
        dlg.classList.remove('is-open');
        setTimeout(() => dlg.close(), BS.reduced() ? 0 : 350);
      }
    });
    /* close and cancel do not bubble, so listen in the capture phase (the block can be re-rendered by variant changes) */
    document.addEventListener(
      'close',
      (e) => {
        if (e.target instanceof HTMLDialogElement && e.target.hasAttribute('data-bs-size-guide')) BS.unlock(e.target);
      },
      true
    );
    document.addEventListener(
      'cancel',
      (e) => {
        const dlg = e.target;
        if (!(dlg instanceof HTMLDialogElement) || !dlg.hasAttribute('data-bs-size-guide')) return;
        e.preventDefault();
        dlg.classList.remove('is-open');
        setTimeout(() => dlg.close(), BS.reduced() ? 0 : 350);
      },
      true
    );
  };

  const readMore = () => {
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-bs-more-toggle]');
      if (!t) return;
      const box = t.closest('[data-bs-more]');
      if (!box) return;
      box.classList.add('is-open');
      t.setAttribute('aria-expanded', 'true');
      t.hidden = true;
    });
  };

  BS.initProductHelpers = () => {
    countdowns();
    sizeGuides();
    readMore();
  };
})();

/* ---- 90-boot.js ---- */
/* Boot: preloader hand-over, section start-up, theme editor events. */
(() => {
  'use strict';

  const BS = window.BStore;
  const { $, $$ } = BS;

  const refresh = () => {
    const ST = BS.ST();
    if (!ST) return;
    try {
      ST.sort();
      ST.refresh();
    } catch (e) {
      /* ignore */
    }
    document.dispatchEvent(new CustomEvent('bstore:refresh'));
  };
  BS.refresh = refresh;

  const runPreloader = () => {
    const pl = document.getElementById('BsPreloader');
    const html = document.documentElement;
    if (!pl) {
      html.classList.remove('bs-is-loading');
      return;
    }
    const gsap = BS.gsap();
    const bar = $('[data-pl-bar]', pl);
    const count = $('[data-pl-count]', pl);
    const heroImgs = $$('img.bs-hero__jacket').slice(0, 2);
    const ready = Promise.all([
      document.fonts && document.fonts.ready ? document.fonts.ready.catch(() => {}) : Promise.resolve(),
      ...heroImgs.map((im) => (im.decode ? im.decode().catch(() => {}) : Promise.resolve())),
    ]);
    const timeout = new Promise((r) => setTimeout(r, 2400));
    const done = () => {
      pl.dataset.done = '1';
      html.classList.remove('bs-is-loading');
      document.dispatchEvent(new CustomEvent('bstore:preloaded'));
    };

    if (!gsap || BS.reduced()) {
      Promise.race([ready, timeout]).then(() => {
        pl.remove();
        done();
      });
      return;
    }

    const prog = { v: 0 };
    const pTween = gsap.to(prog, {
      v: 100,
      duration: 1,
      ease: 'power2.inOut',
      onUpdate: () => {
        if (bar) bar.style.transform = 'scaleX(' + prog.v / 100 + ')';
        if (count) count.textContent = Math.round(prog.v);
      },
    });
    Promise.race([Promise.all([ready, new Promise((r) => pTween.eventCallback('onComplete', r))]), timeout]).then(() => {
      if (pl.dataset.done) return;
      if (bar) bar.style.transform = 'scaleX(1)';
      if (count) count.textContent = '100';
      pl.dataset.done = '1';
      html.classList.remove('bs-is-loading');
      refresh();
      const tl = gsap.timeline({ onComplete: () => pl.remove() });
      tl.to($('[data-pl-inner]', pl), { y: -30, opacity: 0, duration: 0.45, ease: 'power2.in' }, 0)
        .to($('[data-pl-seam]', pl), { backgroundSize: '2px 100%', duration: 0.45, ease: 'expo.inOut' }, 0.05)
        .to($('.bs-preloader__half--l', pl), { xPercent: -101, duration: 0.9, ease: 'expo.inOut' }, 0.35)
        .to($('.bs-preloader__half--r', pl), { xPercent: 101, duration: 0.9, ease: 'expo.inOut' }, 0.35)
        .to($('[data-pl-seam]', pl), { opacity: 0, duration: 0.3 }, 0.5)
        .add(() => document.dispatchEvent(new CustomEvent('bstore:preloaded')), 0.62);
      const hdr = $$('.bs-hdr__logo, .bs-hdr__nav, .bs-hdr__actions');
      if (hdr.length) tl.fromTo(hdr, { y: -16, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, ease: 'expo.out', stagger: 0.06, clearProps: 'opacity,transform' }, 0.85);
      const ann = $('.bs-announce');
      if (ann) tl.fromTo(ann, { opacity: 0 }, { opacity: 1, duration: 0.8, clearProps: 'opacity' }, 0.85);
    });
  };

  const boot = () => {
    const gsap = BS.gsap();
    const ST = BS.ST();
    if (gsap && ST) {
      gsap.registerPlugin(ST);
      ST.config({ ignoreMobileResize: true });
    }

    BS.initLenis();
    BS.initCursor();
    if (BS.initQuickView) BS.initQuickView();
    BS.initAll();
    BS.initZips();
    BS.reveal(document);
    BS.initDots();
    if (BS.initProductHelpers) BS.initProductHelpers();
    runPreloader();

    refresh();
    window.addEventListener('load', refresh, { once: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh).catch(() => {});

    /* re-measure pins when the layout settles (images without dimensions, late fonts) */
    if ('ResizeObserver' in window && ST) {
      let last = document.body.scrollHeight;
      let t = null;
      new ResizeObserver(() => {
        const h = document.body.scrollHeight;
        if (Math.abs(h - last) < 40) return;
        last = h;
        clearTimeout(t);
        t = setTimeout(refresh, 200);
      }).observe(document.body);
    }

    /* ---------- theme editor ---------- */
    document.addEventListener('shopify:section:load', (e) => {
      BS.initAll(e.target);
      BS.initZips();
      BS.reveal(e.target);
      BS.initDots();
      setTimeout(refresh, 60);
    });
    document.addEventListener('shopify:section:unload', (e) => {
      $$('[data-bs-section]', e.target).forEach(BS.destroySection);
      setTimeout(refresh, 60);
    });
    document.addEventListener('shopify:section:reorder', () => {
      BS.initZips();
      BS.initDots();
      setTimeout(refresh, 60);
    });
    document.addEventListener('shopify:block:select', (e) => {
      const block = e.target;
      const root = block.closest && block.closest('[data-bs-section]');
      if (!root) return;
      const inst = BS.instance(root);
      const index = block.dataset.i != null ? block.dataset.i : block.dataset.sc;
      if (inst && inst.select && index != null) inst.select(Number(index));
    });
    document.addEventListener('shopify:section:select', (e) => {
      const root = e.target.querySelector && e.target.querySelector('[data-bs-section="hero"]');
      const inst = root && BS.instance(root);
      if (inst && inst.pause) inst.pause('editor');
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      const root = e.target.querySelector && e.target.querySelector('[data-bs-section="hero"]');
      const inst = root && BS.instance(root);
      if (inst && inst.resume) inst.resume('editor');
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
